import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import {
  XMarkIcon,
  // ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArchiveBoxXMarkIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  // QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import TrailMenuDialog from './TrailMenuDialog';
import { RootState } from '../../store';
import { openStorySelector } from '../../store/trail';
import {
  arrivalSoundsMuted,
  setArrivalSoundsMuted,
} from '../Phone/arrivalSound';
export interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Phone shell only: offer the new-message sound switch. */
  showMessageSounds?: boolean;
}

export default function TrailMenu({
  open,
  setOpen,
  showMessageSounds = false,
}: Props) {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState<string | null>(null);
  const [soundsMuted, setSoundsMuted] = useState(() => arrivalSoundsMuted());
  const { currentTrail } = useSelector((state: RootState) => state.trail);
  const canSwitchStory = (currentTrail?.stories.length || 0) > 1;

  const navigation = [
    // {
    //   name: 'Help',
    //   id: 'help',
    //   icon: QuestionMarkCircleIcon,
    // },
    {
      name: 'About',
      id: 'about',
      icon: InformationCircleIcon,
    },
    // {
    //   name: 'Reset story progress',
    //   id: 'resetStoryProgress',
    //   icon: ArrowPathIcon,
    // },
    ...(canSwitchStory
      ? [
          {
            name: 'Switch story',
            id: 'switchStory',
            icon: ArrowsRightLeftIcon,
          },
        ]
      : []),
    {
      name: 'Privacy choices',
      id: 'privacyChoices',
      icon: ShieldCheckIcon,
    },
    {
      name: 'Delete all saved data',
      id: 'deleteAllSavedData',
      icon: ArchiveBoxXMarkIcon,
    },
  ];

  return (
    <>
      <Transition show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpen}>
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <TransitionChild
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <DialogPanel className="pointer-events-auto w-screen max-w-md">
                    <div className="bg-mt-surface flex h-full flex-col overflow-y-scroll py-6 shadow-xl">
                      <div className="px-4 sm:px-6">
                        <div className="flex items-start justify-between">
                          <DialogTitle className="text-mt-ink text-base leading-6 font-semibold">
                            Menu
                          </DialogTitle>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="bg-mt-surface text-mt-ink-faint hover:text-mt-ink-muted focus:ring-mt-primary relative rounded-md focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
                              onClick={() => setOpen(false)}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="relative mt-6 flex-1 px-4 sm:px-6">
                        <ul role="list" className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <button
                                onClick={() => {
                                  if (
                                    item.id === 'switchStory' &&
                                    currentTrail
                                  ) {
                                    dispatch(
                                      openStorySelector({
                                        trail: currentTrail,
                                        stories: currentTrail.stories,
                                        source: 'loaded',
                                      })
                                    );
                                    setOpen(false);
                                    return;
                                  }

                                  setDialog(item.id);
                                }}
                                className="group text-mt-ink-soft hover:bg-mt-surface-2 hover:text-mt-primary flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                              >
                                <item.icon
                                  className="text-mt-ink-faint group-hover:text-mt-primary h-6 w-6 shrink-0"
                                  aria-hidden="true"
                                />
                                {item.name}
                              </button>
                            </li>
                          ))}
                        </ul>
                        {showMessageSounds && (
                          <label
                            htmlFor="menu-message-sounds"
                            className="text-mt-ink-soft mt-4 flex items-center gap-x-3 p-2 text-sm leading-6 font-semibold"
                          >
                            <input
                              id="menu-message-sounds"
                              type="checkbox"
                              checked={!soundsMuted}
                              onChange={(event) => {
                                setArrivalSoundsMuted(!event.target.checked);
                                setSoundsMuted(!event.target.checked);
                              }}
                              className="border-mt-border text-mt-primary focus:ring-mt-primary h-4 w-4 rounded"
                            />
                            Play a sound when a message arrives
                          </label>
                        )}
                      </div>
                      <p className="text-mt-ink-muted mt-6 px-4 text-sm sm:px-6">
                        Run your own trails at{' '}
                        <a
                          href="https://magicaltrails.com/?utm_source=player&utm_medium=trail-menu"
                          target="_blank"
                          rel="noopener"
                          className="text-mt-primary font-semibold underline"
                        >
                          magicaltrails.com
                        </a>
                      </p>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
      <TrailMenuDialog dialog={dialog} close={() => setDialog(null)} />
    </>
  );
}
